import React from 'react';
import { ArrowRight, BookOpen, Bot, FileText, GitBranch, Layers, Network, Palette, ShieldCheck, Workflow, Zap } from 'lucide-react';
import { Eyebrow, InvestorsContactCta, InvestorsSubnav } from './InvestorsShared';

type IconType = React.ComponentType<{ size?: number; className?: string }>;

const documents: { title: string; description: string; href: string; icon: IconType; meta: string }[] = [
  {
    title: '10-year vision',
    description: 'Where software is going, what we are building for it, and the three stages we get there in.',
    href: '/investors/vision',
    icon: BookOpen,
    meta: 'Essay',
  },
  {
    title: 'Technical deep dive',
    description: 'Architecture, routing, GPU placement, security, economics, roadmap, and risks.',
    href: '/investors/deep-dive',
    icon: FileText,
    meta: '17 chapters',
  },
  {
    title: 'Security',
    description: 'Isolation, secret handling, spend limits, and audit trails.',
    href: '/security',
    icon: ShieldCheck,
    meta: 'Product page',
  },
  {
    title: 'Docs and API',
    description: 'The reference customers build against.',
    href: '/docs',
    icon: GitBranch,
    meta: 'Reference',
  },
];

const today = [
  {
    icon: Bot,
    title: 'Coding agents',
    body: 'Agents branch, edit, run tests, and open pull requests on hosted repos. Schedulers run them on cron; auto-agents run them on events.',
  },
  {
    icon: Layers,
    title: 'One account',
    body: 'Repos, model gateway, GPU compute, hosting, CI, search, and editors bill to one prepaid balance under one key.',
  },
  {
    icon: Zap,
    title: 'Model and GPU routing',
    body: 'The gateway fronts 20+ providers. Each request goes to the best model and the cheapest capable GPU, with automatic fallback.',
  },
  {
    icon: Palette,
    title: 'Editors',
    body: 'Video, audio, image, vector, slides, sheets, docs, 3D, animation, and whiteboard. Every operation is a tool an agent can call.',
  },
];

const plan: { icon: IconType; title: string; body: string; href: string }[] = [
  {
    icon: Network,
    title: 'Infrastructure that optimizes itself',
    body: 'A learned router picks the model and the GPU per request, within about a millisecond, across every provider we can reach. Customers stop making infrastructure decisions.',
    href: '/investors/deep-dive#auto-optimizing-infra',
  },
  {
    icon: Workflow,
    title: 'Agents that run for months',
    body: 'Agents with durable memory, standing budgets, and escalation rules. They watch queues, patch dependencies, hold latency targets, and keep shipping.',
    href: '/investors/deep-dive#run-forever-agents',
  },
  {
    icon: Bot,
    title: 'Software that maintains itself',
    body: 'Systems that read their own telemetry, propose and test their own fixes, and merge through review gates a human set once.',
    href: '/investors/deep-dive#self-organizing-software',
  },
];

const facts = [
  { k: 'model providers behind one API', v: '20+' },
  { k: 'editors and studios live', v: '12' },
  { k: 'GPU entry price, billed per second', v: '$0.228/hr' },
  { k: 'credit balance for everything', v: '1' },
];

export const InvestorsPage: React.FC = () => (
  <main className="overflow-hidden bg-black text-white">
    <section className="relative border-b border-white/10 px-4 pb-14 pt-24 sm:px-8 lg:px-12">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.12),transparent_55%)]" />
      <div className="relative mx-auto max-w-5xl">
        <Eyebrow>Investors</Eyebrow>
        <h1 className="mt-4 max-w-[18ch] font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
          The cloud where agents build and run software.
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-8 text-white/72">
          app.nz gives an agent everything it needs on one account: repos, models, GPUs, hosting, CI,
          search, and editors. We are a small technical team in New Zealand. The platform is live and
          billing customers, and we build it with the same agents we sell.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="/investors/vision" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-6 text-sm font-bold text-black transition hover:bg-sky-300">
            Read the vision <ArrowRight size={16} />
          </a>
          <a href="/investors/deep-dive" className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition hover:bg-white hover:text-black">
            Technical deep dive
          </a>
        </div>
        <div className="mt-10">
          <InvestorsSubnav active="/investors" />
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <Eyebrow>Why</Eyebrow>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Agents will write and run most software</h2>
        <p className="mt-5 max-w-3xl text-base leading-8 text-white/72">
          Writing code is getting cheap. The work around it is not: repos, compute, models, deploys,
          review, and the bill. One agent session touches all of them, so they belong on one account.
        </p>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((f) => (
            <div key={f.k} className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
              <p className="font-display text-2xl font-bold text-sky-400">{f.v}</p>
              <p className="mt-1 text-xs leading-5 text-white/55">{f.k}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <Eyebrow>Live today</Eyebrow>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">What customers use now</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {today.map((p) => (
            <div key={p.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
              <p.icon size={20} className="text-sky-400" />
              <h3 className="mt-4 font-display text-lg font-bold">{p.title}</h3>
              <p className="mt-2 text-sm leading-7 text-white/68">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <Eyebrow>The plan</Eyebrow>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Where this goes</h2>
        <p className="mt-5 max-w-3xl text-base leading-8 text-white/72">
          Three things we are building toward. Each starts from something live today. The deep dive says
          what has shipped, what is next, and what is still research.
        </p>
        <div className="mt-8 grid gap-4 lg:grid-cols-3">
          {plan.map((b) => (
            <a key={b.title} href={b.href} className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition hover:border-sky-400/40 hover:bg-white/[0.05]">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-sky-400/25 bg-sky-400/10">
                <b.icon size={18} className="text-sky-400" />
              </div>
              <h3 className="mt-4 font-display text-lg font-bold group-hover:text-sky-300">{b.title}</h3>
              <p className="mt-2 text-sm leading-7 text-white/68">{b.body}</p>
            </a>
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <Eyebrow>Documents</Eyebrow>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Read more</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {documents.map((d) => (
            <a key={d.title} href={d.href} className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition hover:border-sky-400/40 hover:bg-white/[0.05]">
              <div className="flex items-center justify-between">
                <d.icon size={20} className="text-sky-400" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">{d.meta}</span>
              </div>
              <h3 className="mt-4 font-display text-xl font-bold group-hover:text-sky-300">{d.title}</h3>
              <p className="mt-2 text-sm leading-7 text-white/68">{d.description}</p>
            </a>
          ))}
        </div>
      </div>
    </section>

    <div className="px-4 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <InvestorsContactCta />
      </div>
    </div>
  </main>
);

export default InvestorsPage;
